/**
 * PYMT-2285 — v1 payment-info data source (first migration step).
 *
 * Fetches `GET /api/v1/payment-info/` and returns the server-localized,
 * display-ready payload (screen title + installments). The browser never holds
 * or mints credentials: the request's `Authorization` header (payjoy_www JWT or
 * V2 HMAC) is injected by the hosting app/webview at the network layer, and the
 * server derives the device from that authenticated token — not from any value
 * the client sends. For local/QA testing an admin mints a JWT via the
 * admin-session-guarded `GET /api/v1/auth/token/` endpoint (non-prod) and sets
 * it as the Bearer header in Postman.
 */
import axios, { AxiosInstance } from "axios";
import { snakeToCamel } from "@/shared-helpers/snake-to-camel.helper";
import { IV1PaymentInfo } from "@/types/IV1PaymentInfo";
import { IV1ProvidersResponse } from "@/types/IV1Providers";
import { IV1PaymentReferenceRequest, IV1PaymentReferenceResponse } from "@/types/IV1PaymentReference";

/**
 * Resolve the v1 API base URL.
 *
 * Prefers an explicit `VUE_APP_MIDDLEWARE_V1_URL`; otherwise derives it from the
 * legacy middleware origin (`.../middleware/mobile/` → `.../api/v1/`), falling
 * back to a relative `/api/v1/` when the legacy URL is unset or malformed.
 */
function resolveV1BaseUrl(): string {
  const explicit = process.env.VUE_APP_MIDDLEWARE_V1_URL;
  if (explicit) {
    return explicit.replace(/\/?$/, "/");
  }
  const legacy = process.env.VUE_APP_MIDDLEWARE_URL || "";
  try {
    return `${new URL(legacy).origin}/api/v1/`;
  } catch {
    return "/api/v1/";
  }
}

const v1Api: AxiosInstance = axios.create({
  baseURL: resolveV1BaseUrl(),
  timeout: 10000,
  // Django's CSRF names, so the non-prod admin-session mint (a POST) passes
  // SessionAuthentication's CSRF check. axios reads the JS-readable `csrftoken`
  // cookie and echoes it as `X-CSRFToken` — but only when the mint call opts in
  // with `withXSRFToken: true`, since the mint is cross-origin (:8080 -> :8000)
  // and axios 1.6+ otherwise skips the header on cross-origin requests.
  xsrfCookieName: "csrftoken",
  xsrfHeaderName: "X-CSRFToken",
});

/**
 * Non-production dev/QA convenience: obtain a v1 JWT from the admin-session
 * guarded mint endpoint and set it as the default `Authorization` header on the
 * v1 client — simulating the header the native app injects in production.
 *
 * Relies on the developer's Django admin session (sent via `withCredentials`),
 * so no credentials live in the bundle and the mint is bound to a proven staff
 * identity. It is a **no-op in production**, where the app injects the header
 * and the mint endpoint isn't registered. Failures (e.g. not logged into the
 * admin) are swallowed so the view can still render its error state.
 *
 * Called from the `/v1/device/:deviceTag` route guard, mirroring how the legacy
 * devices section establishes auth on route entry.
 *
 * @param deviceTag - The device to mint a token for (admin-chosen; the actual
 *   payment-info call still derives the device from the validated token).
 */
export async function ensureV1DevAuth(deviceTag: string): Promise<void> {
  // Only the local dev server mints a token; any build (staging/prod) gets the
  // header from the host app. NODE_ENV is "production" in every build.
  if (process.env.NODE_ENV === "production") {
    return;
  }
  try {
    const response = await v1Api.post(
      "auth/token/",
      { device_tag: deviceTag },
      { withCredentials: true, withXSRFToken: true },
    );
    v1Api.defaults.headers.common.Authorization = `Bearer ${response.data.token}`;
  } catch (error) {
    console.warn("[v1] dev auth mint failed — are you logged into the Django admin?", error);
  }
}

/**
 * Fetch the v1 payment context from `GET /api/v1/payment-info/`.
 *
 * Sends no credentials and no device identifier — auth is carried by the
 * `Authorization` header the hosting app/webview injects in production (or, in
 * non-prod, the one `ensureV1DevAuth` set on this client), and the device is
 * resolved server-side from that token. Returns the camelCased payload; errors
 * propagate for the caller to surface.
 */
export async function fetchV1PaymentInfo(): Promise<IV1PaymentInfo> {
  const response = await v1Api.get("payment-info/");
  return snakeToCamel<IV1PaymentInfo>(response.data);
}

/**
 * Fetch the v1 providers context from `GET /api/v1/providers/`.
 *
 * The response is already filtered, grouped, sorted and localized server-side, so
 * the caller renders the sections verbatim. Requires an existing session, so
 * `fetchV1PaymentInfo` must run first. The selected payment `amount` (and whether
 * it is a custom amount) drive per-provider availability server-side. Returns the
 * camelCased payload; errors propagate for the caller to surface.
 */
export async function fetchV1Providers(params?: { amount?: string; custom?: boolean }): Promise<IV1ProvidersResponse> {
  const response = await v1Api.get("providers/", { params });
  return snakeToCamel<IV1ProvidersResponse>(response.data);
}

/**
 * Build an RFC 4122 v4 UUID from `crypto.getRandomValues`.
 *
 * `crypto.randomUUID` would be simpler but isn't in the project's TypeScript
 * `Crypto` lib types (TS < 4.6) and isn't guaranteed on older Android WebViews;
 * `getRandomValues` is. The version/variant nibbles are forced so the value
 * satisfies the server's UUIDv4 idempotency-key check.
 */
function uuidV4(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10xx
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0"));
  return [
    hex.slice(0, 4).join(""),
    hex.slice(4, 6).join(""),
    hex.slice(6, 8).join(""),
    hex.slice(8, 10).join(""),
    hex.slice(10, 16).join(""),
  ].join("-");
}

/**
 * Generate a payment reference from `POST /api/v1/payment-reference/` (EP-3).
 *
 * The response is a discriminated union keyed on `type` (`reference` | `redirect`
 * | `qr` | `virtual_account`), already server-localized and display-ready. The
 * caller renders it by `type` alone — no provider-specific logic. Requires an
 * existing session, so `fetchV1PaymentInfo` must run first.
 *
 * A fresh **UUIDv4** `Idempotency-Key` is minted per call: the server's two-phase
 * dispatch keys on it so a retried request never creates a duplicate reference.
 * The body is sent snake_cased to match the request serializer; the response is
 * camelCased back for the client. Errors propagate for the caller to surface.
 */
export async function fetchV1PaymentReference(
  request: IV1PaymentReferenceRequest,
): Promise<IV1PaymentReferenceResponse> {
  const body: Record<string, unknown> = {
    provider_id: request.providerId,
    amount: request.amount,
    days: request.days,
  };
  if (request.sectionId) {
    body.section_id = request.sectionId;
  }
  const response = await v1Api.post("payment-reference/", body, {
    headers: { "Idempotency-Key": uuidV4() },
  });
  return snakeToCamel<IV1PaymentReferenceResponse>(response.data);
}
